import React, {FC, useEffect} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {Col, Modal, ModalBody, ModalHeader, ModalTitle, Row} from 'react-bootstrap'
import {ErrorMessage, Form, Formik} from 'formik'
import * as Yup from 'yup'
import Select from 'react-select'
import {assignSupplierToPackageItinerary} from '../../../../setup/redux/bookings/actions'
import {getCurrencyDropdown, getSuppliers} from '../../../../setup/redux/suppliers/actions'
import {RootState} from '../../../../setup'
import {postRequest} from '../../../../setup/redux/common/cruds'
import {
  AssignedSupplier,
  serviceSupplierInitialValues,
  supplierSwapRemovalId,
} from './supplierAssignment'

interface assignSupplier {
  show: boolean
  hide: any
  itinerary: any
  refetch: any
  supplier?: AssignedSupplier
}

export const ManagePackageItinerarySupplier: FC<assignSupplier> = ({
  show,
  hide,
  itinerary,
  refetch,
  supplier,
}) => {
  const dispatch = useDispatch()
  const isEditing = Boolean(supplier)
  const assignSupplier = {
    itinerary_id: itinerary.id,
    ...(isEditing
      ? serviceSupplierInitialValues(supplier)
      : {
          supplier_id: '',
          purchase_currency: '',
          purchase_price: itinerary.purchase_price_original || '',
          description: '',
        }),
  }
  const assignSupplierSchema = Yup.object().shape({
    supplier_id: Yup.string().required('Supplier is required'),
  })

  const cityId = itinerary.city_from?.id || itinerary.city_to?.id

  useEffect(() => {
    if (cityId) {
      dispatch(getSuppliers('city', cityId))
    }
    dispatch(getCurrencyDropdown())
  }, [])

  const {supplierLists, currenciesDropdown}: any = useSelector<RootState>(
    (state) => ({
      supplierLists: state.suppliers.supplierLists,
      currenciesDropdown: state.suppliers.currenciesDropdown,
    }),
    shallowEqual
  )

  return (
    <Modal show={show} centered={true} onHide={hide}>
      <ModalHeader closeButton>
        <ModalTitle>
          {isEditing ? 'Update Supplier' : 'Assign Supplier'} — Day {itinerary.day}{' '}
          {itinerary.service}
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        {!cityId && (
          <p className='text-warning'>
            This itinerary service has no city set, so no suppliers can be listed.
          </p>
        )}
        <Formik
          enableReinitialize={true}
          initialValues={assignSupplier}
          validationSchema={assignSupplierSchema}
          onSubmit={async (values) => {
            // The assign endpoint UPDATEs the join row for a supplier that is already attached, so
            // only a genuine swap needs the old assignment torn down first.
            const removeId = supplierSwapRemovalId(supplier, values.supplier_id)
            await dispatch(assignSupplierToPackageItinerary(values))
            if (removeId !== null) {
              await postRequest(
                `${process.env.REACT_APP_API_BASE_URL}bookings/remove-package-itinerary-supplier`,
                {itinerary_id: itinerary.id, supplier_id: removeId}
              )
            }
            refetch()
            hide()
          }}
        >
          {({values, handleChange, handleBlur, handleSubmit}) => (
            <React.Fragment>
              <Form className='form'>
                <Row className={'px-2'}>
                  <Col md={12}>
                    <div className={'form-group mb-3'}>
                      <label className='form-label'>
                        Supplier <span className='text-danger'>*</span>
                      </label>
                      <Select
                        options={supplierLists}
                        value={
                          supplierLists.find(
                            (option: {value: string}) =>
                              Number(option.value) === Number(values.supplier_id)
                          ) || null
                        }
                        name='supplier_id'
                        className='react-select-container'
                        classNamePrefix='react-select'
                        placeholder='Select a supplier'
                        onChange={(option) => {
                          handleChange({target: {name: 'supplier_id', value: option?.value}})
                        }}
                        onBlur={() => {
                          handleBlur({target: {name: 'supplier_id'}})
                        }}
                      />
                      <ErrorMessage
                        name={`supplier_id`}
                        component='div'
                        className='text-danger mt-2'
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className={'form-group mb-3'}>
                      <label className='form-label'>Purchase Currency</label>
                      <Select
                        options={currenciesDropdown}
                        className='react-select-container'
                        classNamePrefix='react-select'
                        value={
                          currenciesDropdown.find(
                            (c: any) => c.name === values.purchase_currency
                          ) || null
                        }
                        name='purchase_currency'
                        placeholder='Select a currency'
                        onChange={(option: any) => {
                          handleChange({
                            target: {name: 'purchase_currency', value: option?.name || ''},
                          })
                        }}
                        onBlur={() => handleBlur({target: {name: 'purchase_currency'}})}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className={'form-group mb-3'}>
                      <label className='form-label'>Purchase Price</label>
                      <input
                        type='number'
                        step='0.01'
                        name='purchase_price'
                        className='form-control'
                        placeholder='Enter price'
                        value={values.purchase_price}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className={'form-group mb-3'}>
                      <label className='form-label'>Description</label>
                      <input
                        type='text'
                        name='description'
                        className='form-control'
                        placeholder='Enter description'
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                  </Col>
                </Row>
                <Row className={'float-end'}>
                  <div className='col-md-12 '>
                    <div className='form-group'>
                      <button
                        type='button'
                        name='submit'
                        className='btn btn-primary me-2'
                        onClick={() => handleSubmit()}
                      >
                        {isEditing ? 'Update' : 'Assign Now'}
                      </button>
                    </div>
                  </div>
                </Row>
              </Form>
            </React.Fragment>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  )
}
